import React, { useEffect, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import AutomationTable from "./AutomationTable";
import AutomationFormModal from "./AutomationFormModal";
import { emptyRecord, getLaunchId, toFormRecord } from "./automationConfigs";

const ScheduledAutomationTab = ({
  config,
  notificationTypeOptions,
  typeOptions,
  ItemsView = AutomationTable,
  onAdd,
  newTypeId,
  showToolbar = true,
  showItems = true,
  showNew = true,
  externalEditItem = null,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingItem, setEditingItem] = useState(null); // null = modal closed
  const [launchStatus, setLaunchStatus] = useState(
    config.launchStatusOptions?.[0] || null,
  );
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (newTypeId == null) return;
    setEditingItem({
      ...emptyRecord(config),
      [config.typeIdField]:
        typeof newTypeId === "object" ? newTypeId.id : newTypeId,
    });
  }, [newTypeId, config]);

  useEffect(() => {
    if (externalEditItem) {
      setEditingItem(toFormRecord(config, externalEditItem));
    }
  }, [externalEditItem, config]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await config.api.list();
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Couldn't load ${config.labelPlural.toLowerCase()}.`,
      );
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    load();
    setSelectedIds(new Set());
    setLaunchStatus(config.launchStatusOptions?.[0] || null);
    return undefined;
  }, [load, config, showItems]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = (checked) =>
    setSelectedIds(
      checked
        ? new Set(
            items.map((i) => getLaunchId(config, i)).filter((id) => id != null),
          )
        : new Set(),
    );

  const handleLaunchSelected = async () => {
    if (!selectedIds.size) return;
    setLaunching(true);
    try {
      // `launchStatus` is only meaningful for resources with launchStatusOptions
      // (currently: Scheduled Events); config.api.launch ignores the extra
      // arg when it doesn't need it (Scheduled Cron Jobs).
      await config.api.launch(Array.from(selectedIds), launchStatus);
      const verb = launchStatus === "ON_HOLD" ? "put on hold" : "launched";
      toast.success(
        `${selectedIds.size} ${config.labelPlural.toLowerCase()} ${verb}.`,
      );
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Couldn't update the selected items.",
      );
    } finally {
      setLaunching(false);
    }
  };

  const handleSaved = () => {
    setEditingItem(null);
    load();
  };

  const handleDelete = async (item) => {
    // The delete endpoints take the nested notification/automation id
    // (same id used for launching), not scheduledEventId/scheduledCronJobId.
    const launchId = getLaunchId(config, item);
    if (launchId == null) {
      toast.error(
        `This ${config.label.toLowerCase()} hasn't been launched yet, so there's nothing to delete.`,
      );
      return;
    }
    if (
      !window.confirm(
        `Delete "${item.taskName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await config.api.delete(launchId);
      toast.success(`${config.label} deleted.`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(launchId);
        return next;
      });
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Couldn't delete this ${config.label.toLowerCase()}.`,
      );
    }
  };

  return (
    <div className="automation-tab">
      {showToolbar && <div className="automation-tab__toolbar">
        <div className="automation-tab__toolbar-left">
          {showNew && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() =>
                onAdd ? onAdd() : setEditingItem(emptyRecord(config))
              }
            >
              <Icon icon="mdi:plus" width="18" />
              New
            </button>
          )}

          {config.launchStatusOptions && (
            <select
              className="launch-status-select"
              value={launchStatus}
              onChange={(e) => setLaunchStatus(e.target.value)}
              aria-label="Status to apply"
            >
              {config.launchStatusOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "ON_HOLD" ? "Put on hold" : "Launch"}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            className="btn btn--secondary"
            disabled={!selectedIds.size || launching}
            onClick={handleLaunchSelected}
          >
            <Icon icon="mdi:rocket-launch-outline" width="18" />
            {launching
              ? "Updating…"
              : `${launchStatus === "ON_HOLD" ? "Put on hold" : "Launch"}${
                  selectedIds.size ? ` (${selectedIds.size})` : ""
                }`}
          </button>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={load}
          aria-label="Refresh"
          disabled={loading}
        >
          <Icon
            icon="mdi:refresh"
            width="20"
            className={loading ? "spin" : ""}
          />
        </button>
      </div>}

      {showItems && (
        <ItemsView
          config={config}
          items={items}
          loading={loading}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onEdit={(item) => setEditingItem(toFormRecord(config, item))}
          onDelete={handleDelete}
        />
      )}

      {editingItem && (
        <AutomationFormModal
          config={config}
          initialData={editingItem}
          notificationTypeOptions={notificationTypeOptions}
          typeOptions={typeOptions}
          onClose={() => setEditingItem(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default ScheduledAutomationTab;
